import { createAsyncThunk, createSlice, } from '@reduxjs/toolkit'
import { AppState } from '@app/store/state/app.state';
import { BasicUserInfo, User } from '@shared/models/user.models';
import { Auth } from '@shared/services/Auth';
import { AuthType } from '@shared/utils/AuthUtils';
import { requestUsersHierarchy } from '@shared/services/CompanyHierarchyService';
import { TenantResponse } from '@shared/services/AccessService';
import { IconPack } from '@shared/models/company.models';
import { isInTeams } from '@shared/utils/TeamsUtils';
import { TrackJS } from 'trackjs';
import { msalInstance } from '@shared/services/msalInstance';

enum TeamsAuthState {
  Loading = 0, SignedOut = 1, SignedIn = 2
}

export interface AuthState {
  currentUser?: User;
  authType: AuthType | null;
  rememberMe: boolean;
  errorMessage?: string;
  redirectTo?: string;
  loading: boolean;
  registerAuthType?: AuthType;
  teamsAuthState?: TeamsAuthState;
  hasTeamsLoaded: boolean;
  isInTeamsApp: boolean;
  isInTeams: boolean;
  currentUsersLineReports: BasicUserInfo[];
  tenantInfo?: TenantResponse;
  calendarSyncActive: boolean;
}

export const initialAuthState: AuthState = {
  currentUser: undefined,
  authType: (localStorage.getItem('type') as AuthType) || null,
  rememberMe: !!localStorage.getItem('rememberMe'),
  errorMessage: undefined,
  redirectTo: undefined,
  loading: true,
  registerAuthType: undefined,
  hasTeamsLoaded: false,
  teamsAuthState: undefined,
  isInTeamsApp: false,
  isInTeams: isInTeams(),
  currentUsersLineReports: [],
  calendarSyncActive: false,
}

function hasAuthCredentials(): boolean {
  if (msalInstance.getAllAccounts().length > 0) {
    return true;
  }
  return !!localStorage.getItem('idToken');
}

const current = createAsyncThunk(
  'auth/current',
  async (passive: boolean = false, thunkAPI) => {
    await thunkAPI.dispatch(setAuthLoading(true));
    try {
      if (hasAuthCredentials()) {
        const user = await Auth.current(passive);
        await thunkAPI.dispatch(setUser(user));
        if (TrackJS?.isInstalled() && !!user) {
          TrackJS?.configure({ userId: user.email });
        }
      } else {
        thunkAPI.dispatch(reset());
      }
    } finally {
      thunkAPI.dispatch(setAuthLoading(false));
    }
  }
)

export const currentUserLineReports: any = createAsyncThunk(
  'auth/currentUserLineReports',
  async (none: boolean, thunkAPI) => {
    const currentUser = (thunkAPI.getState() as AppState).auth.currentUser;
    if (currentUser && currentUser.companyEntity.lineManagementEnabled) {
      const structure = await requestUsersHierarchy(currentUser.id);
      return structure.lineReports;
    } else {
      return [];
    }
  }
)

export const updateCurrentUser = createAsyncThunk(
  'auth/current',
  async (force: boolean = false, thunkAPI) => {
    const appState = thunkAPI.getState() as AppState;
    try {
      if (!force && appState.auth.currentUser) {
        return;
      }

      await thunkAPI.dispatch(setAuthLoading(true));

      if (hasAuthCredentials()) {
        const user = await Auth.current(false);
        await thunkAPI.dispatch(setUser(user));
        if (TrackJS?.isInstalled() && !!user) {
          TrackJS?.configure({ userId: user.email });
        }
      } else {
        thunkAPI.dispatch(reset());
      }
    } finally {
      thunkAPI.dispatch(setAuthLoading(false));
    }
  }
)

// @ts-ignore
const authSlice = createSlice({
  name: 'auth',
  initialState: initialAuthState,
  reducers: {
    reset: (state) => ({
      ...initialAuthState,
      loading: state.loading
    }),
    clearRegistration: (state) => ({
      ...initialAuthState,
      loading: state.loading,
      currentUser: state.currentUser,
      rememberMe: state.rememberMe,
      hasTeamsLoaded: state.hasTeamsLoaded,
      isInTeamsApp: state.isInTeamsApp,
    }),
    setTenantInfo: (state, action) => ({ ...state, tenantInfo: action.payload }),
    setAuthLoading: (state, action) => ({ ...state, loading: action.payload }),
    setUser: (state, action) => ({ ...state, currentUser: action.payload }),
    setAuthError: (state, action) => ({ ...state, errorMessage: action.payload }),
    setRegisterAuthType: (state, action) => ({ ...state, registerAuthType: action.payload }),
    setIsInTeamsApp: (state, action) => ({ ...state, isInTeamsApp: action.payload }),
    setIsInTeams: (state, action) => ({ ...state, isInTeams: action.payload }),
    redirectTo: (state, action) => ({...state, redirectTo: action.payload}),
    setHasTeamsLoaded: (state, action) => ({...state, hasTeamsLoaded: action.payload}),
    setTeamsProviderState: (state, action) => ({...state, teamsAuthState: action.payload}),
    setAuthType: (state, action) => ({...state, authType: action.payload}),
    setCalendarSyncActive: (state, action) => ({...state, calendarSyncActive: action.payload}),
    closeFirstTimeView: (state) => {
      if (state.currentUser) {
        return {
          ...state,
          currentUser: { ...state.currentUser, firstTime: false },
        }
      } else {
        return state
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(currentUserLineReports.fulfilled, (state, action) => ({...state, currentUsersLineReports: action.payload}));
  }
})

export default authSlice.reducer
export const { setAuthError, setAuthLoading, setAuthType, setRegisterAuthType, setUser, setIsInTeamsApp, setIsInTeams, setTenantInfo, reset, clearRegistration, redirectTo, closeFirstTimeView, setCalendarSyncActive } = authSlice.actions;

// Selectors
export const selectCurrentUser = (state: AppState) => state.auth.currentUser;
export const selectIconPack = (state: AppState) => state.auth.currentUser?.companyEntity.iconPack ?? IconPack.STANDARD;
export const selectCurrentUserLineReports = (state: AppState) => state.auth.currentUsersLineReports;
const selectTeamsProviderState = (state: AppState) => state.auth.teamsAuthState;
export const selectRedirectTo = (state: AppState) => state.auth.redirectTo;
export const selectIsAuthLoading = (state: AppState) => state.auth.loading;
export const selectAuthErrorMessage = (state: AppState) => state.auth.errorMessage;
export const selectRegisterAuthType = (state: AppState) => state.auth.registerAuthType;
export const selectIsInTeamsApp = (state: AppState) => state.auth.isInTeamsApp;
export const selectIsInTeams = (state: AppState) => state.auth.isInTeams;
export const selectTenantInfo = (state: AppState) => state.auth.tenantInfo;
const selectAuthType = (state: AppState) => state.auth.authType;
export const selectIsCalendarSyncActive = (state: AppState) => state.auth.calendarSyncActive;
const selectCurrentUserTeam = (state: AppState) => {
  const mockTeam = () => ({
    id: 'No team assigned',
    companyId: state.auth.currentUser?.companyEntity.id || '',
    name: 'No team assigned'
  });
  return state.auth.currentUser?.teamEntity || mockTeam();
};
